import { useMemo, useState } from "react";
import { Button, Space, Typography } from "antd";
import useReservationConfig from "./hooks/useReservationConfig";
import ExperienceTabs from "./ExperienceTabs";
import ExperiencePreview from "./ExperiencePreview";

const { Text } = Typography;

export default function ExperienceBuilder() {
  const { config, loading, saving, dirty, setConfig, saveDraft, publish } =
    useReservationConfig();

  // default to Branding & Media (since preview visible there)
  const [activeTab, setActiveTab] = useState("brandingMedia");

  // ✅ Preview ONLY on Branding & Media + Publish
  const SHOW_PREVIEW_TABS = useMemo(
    () => new Set(["brandingMedia", "publish"]),
    []
  );

  const showPreview = SHOW_PREVIEW_TABS.has(activeTab);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid" style={{ paddingBottom: 16 }}>
        {/* Header */}
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-7">
              <h3 className="page-title" style={{ marginBottom: 4 }}>
                Reservation Experience
              </h3>
              <div style={{ color: "#6b7280", fontSize: 13 }}>
                Design your restaurant booking journey (widget + dedicated page)
              </div>
            </div>

            <div className="col-md-5 float-end ms-auto">
              <div className="d-flex title-head justify-content-end align-items-center gap-2">
                {!dirty && !loading && (
                  <span
                    className="badge"
                    style={{
                      background: "rgba(34,197,94,0.14)",
                      color: "#16a34a",
                      border: "1px solid rgba(34,197,94,0.25)",
                      padding: "6px 10px",
                      borderRadius: 999,
                      fontWeight: 800
                    }}
                  >
                    Saved
                  </span>
                )}

                {dirty && !loading && (
                  <span
                    className="badge"
                    style={{
                      background: "rgba(245,158,11,0.14)",
                      color: "#b45309",
                      border: "1px solid rgba(245,158,11,0.25)",
                      padding: "6px 10px",
                      borderRadius: 999,
                      fontWeight: 800
                    }}
                  >
                    Unsaved changes
                  </span>
                )}

                <Space>
                  <Button
                    onClick={saveDraft}
                    loading={saving}
                    disabled={loading || !dirty}
                  >
                    Save Draft
                  </Button>

                  <Button
                    type="primary"
                    onClick={publish}
                    loading={saving}
                    disabled={loading || dirty}
                  >
                    Publish
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="row">
          <div className="col-12">
            {/* ✅ Lock layout height, delegate scroll INSIDE panels */}
            <div
              style={{
                display: "flex",
                gap: 16,
                height: "calc(100vh - 190px)",
                overflow: "hidden"
              }}
            >
              {/* LEFT: Editor */}
              <div
                style={{
                  flex: showPreview ? "0 0 60%" : "1 1 100%",
                  minWidth: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                {/* ✅ Card wrapper */}
                <div
                  style={{
                    height: "100%",
                    borderRadius: 14,
                    background: "#fff",
                    border: "1px solid rgba(15,23,42,0.08)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                  {/* ✅ This is the scroll container for all tabs content */}
                  <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    <ExperienceTabs
                      config={config}
                      loading={loading}
                      activeKey={activeTab}
                      onTabChange={setActiveTab}
                      onChange={setConfig}
                    />
                  </div>
                </div>

                {!showPreview && (
                  <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12 }}>
                    Preview is shown only on{" "}
                    <Text strong>Branding & Media</Text> and{" "}
                    <Text strong>Publish</Text>.
                  </div>
                )}
              </div>

              {/* RIGHT: Preview */}
              {showPreview && (
                <div
                  style={{
                    flex: "0 0 40%",
                    minWidth: 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      borderRadius: 14,
                      overflow: "hidden",
                      border: "1px solid rgba(15,23,42,0.08)",
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    <ExperiencePreview
                      config={config}
                      mode={activeTab === "publish" ? "publish" : "builder"}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
